import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { useChangePassword } from './queries';

/**
 * Das eigene Kennwort wechseln -- docs/04, Abschnitt 4.
 *
 * **Es gab diesen Weg nicht.** Die Ausgabe von ``provision_tenant.py``
 * fordert dazu auf, das Erstkennwort nach der ersten Anmeldung zu wechseln --
 * nur ging das nirgends. Ein Erstkennwort, das man behalten muss, ist ein
 * Dauerkennwort, das ein Zweiter kennt.
 *
 * **Das alte Kennwort steht mit im Formular.** Nicht als Formsache: Ein
 * unbeaufsichtigter Rechner mit offener Sitzung waere sonst ein uebernommenes
 * Konto -- der Angreifer setzte ein neues Kennwort und sperrte den Besitzer
 * aus. Dieselbe Ueberlegung wie bei Schritt 1 der Einrichtung des zweiten
 * Faktors.
 *
 * **Die Wiederholung wird hier geprueft und nicht am Server.** Sie ist keine
 * Sicherheitsfrage, sondern eine gegen den Tippfehler -- und ein Tippfehler,
 * den erst der Server meldet, kostet einen Weg hin und zurueck.
 */
export function PasswordPanel() {
  const { t } = useTranslation();
  const change = useChangePassword();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const mismatch = repeat !== '' && next !== repeat;
  const ready = current !== '' && next !== '' && next === repeat;

  return (
    <section className="card">
      <h2>{t('security.password')}</h2>
      <p className="table__muted">{t('security.passwordHint')}</p>

      {change.isSuccess ? (
        <div className="notice notice--warning" role="status">
          <p>{t('security.passwordChanged', { count: change.data.revoked_sessions })}</p>
        </div>
      ) : null}

      {change.isError ? <ErrorNotice error={change.error} /> : null}

      <TextField
        label={t('security.currentPassword')}
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={(event) => {
          setCurrent(event.target.value);
        }}
      />

      <TextField
        label={t('security.newPassword')}
        type="password"
        autoComplete="new-password"
        hint={t('security.newPasswordHint')}
        value={next}
        onChange={(event) => {
          setNext(event.target.value);
        }}
      />

      <TextField
        label={t('security.repeatPassword')}
        type="password"
        autoComplete="new-password"
        error={mismatch ? t('security.passwordMismatch') : undefined}
        value={repeat}
        onChange={(event) => {
          setRepeat(event.target.value);
        }}
      />

      <Button
        variant="primary"
        disabled={!ready}
        busy={change.isPending}
        onClick={() => {
          change.mutate(
            { current_password: current, new_password: next },
            {
              onSuccess: () => {
                // Die Felder werden geleert, auch wenn die Sitzung gleich
                // endet: Ein Kennwort, das im Formular stehen bleibt, steht
                // dort auch fuer den naechsten, der auf den Bildschirm sieht.
                setCurrent('');
                setNext('');
                setRepeat('');
              },
            },
          );
        }}
      >
        {t('security.changePassword')}
      </Button>
    </section>
  );
}
